/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Review Component
 *
 * @file ReviewComponent.jsx
 * @author Murhsid Ahemedh
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Menu, useMediaQuery } from '@mui/material';
import { injectIntl } from 'react-intl';
import DashboardCard from '../dashboardCard/DashboardCard';
import McqComponent from '../common/McqComponent';
import LeftAccordionList from './leftPannel/LeftAccordionList';
import RightPannel from './rightPannel/RightPannel';
import EmptyStateCard from '../common/EmptyStateCard';
import {
  ReviewCardRoot,
  ReviewActionsLeftContainer,
  ReviewContainer,
  ReviewContent,
  ReviewLeftCol,
  ReviewRightCol,
  LeftAccordionListMenuPaper,
  LeftAccordionListMenuList
} from './styles/ReviewComponentStyle';
import { DashboardCardStudyPrepHeader, DashboardCardStudyPrepRoot, DashboardCardStudyPrepTitle } from '../dashboardCard/styles/DashboardCardStyles';
import Utils from '../../common/utils';
import MultiRemediationComponent from '../remediationComponent/MultiRemediationComponent';

const ReviewComponent = (props) => {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const {
    rightContent,
    showViewFlashcards,
    flashcards,
    data,
    onQuestionStatusChange,
    onSelect,
    suggestedRemediationConfig,
    onSeeInChannelsLinkClick,
    remediationContainerProps,
    footer,
    footerDialogDetails,
    footerCTALinks,
    headerTitle,
    threeDotMenuItems
  } = props;
  const [selected, setSelected] = React.useState(null);
  const isScreenBelow1024 = !useMediaQuery('@media screen and (min-width: 1024px)');

  const sections = React.useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const leftAccordionData = React.useMemo(() => sections.map((section) => ({
    name: section.label,
    id: section.id,
    count: section?.topics?.length || 0,
    flashcardCount: flashcards.length || 0,
    topics: section?.topics || []
  })),
  [sections, flashcards]);

  const handleSelect = React.useCallback((sel) => {
    setSelected(sel);
    onSelect(sel);
  }, []);

  const handleStatusChange = React.useCallback((questionId, topic, oldStatus, newStatus, assignmentId) => {
    if (onQuestionStatusChange && selected) {
      onQuestionStatusChange({
        questionId,
        section: selected.section,
        topic,
        oldStatus,
        newStatus,
        assignmentId
      });
    }
  }, [onQuestionStatusChange, selected]);

  const rightPanelContent = React.useMemo(() => {
    if (rightContent) return rightContent;
    if (!selected) return null;

    const suggestedRemediation = suggestedRemediationConfig || {};
    const section = (sections || []).find((s) => s.id?.toLowerCase() === (selected.section || '').toLowerCase());
    const topic = selected.type === 'topic' ? selected.label : selected.section;

    const topicMeta = Array.isArray(section?.topics)
      ? section.topics.find((t) => (typeof t === 'string' ? t === topic : t?.name === topic))
      : null;
    const assets = typeof topicMeta === 'string' ? null : topicMeta?.assets;
    const assignmentsForTopic = topicMeta?.assignments || [];

    if (assignmentsForTopic.length === 0) return null;
    const questions = [];
    assignmentsForTopic.forEach((a) => {
      (a.questions || []).forEach((q) => {
        questions.push({ question: q, assignmentName: a.assignmentName, assignmentId: a.assignmentId });
      });
    });
    if (!questions.length && !assets) return null;

    const getQuestionStatus = (status) => {
      let reviewStatus = null;
      const statusType = sections.filter((type) => Utils.normalizeStatus(type?.id) === Utils.normalizeStatus(status));
      if (statusType?.length > 0) {
        reviewStatus = statusType[0]?.label || null;
      }
      if (!reviewStatus && sections?.length > 0) {
        reviewStatus = sections[0].label;
      }

      return reviewStatus;
    };

    return (
      <ReviewContainer>
        {questions.map(({ question: q, assignmentName, assignmentId }) => (
          <McqComponent
            key={`${q.questionId}-${q.status}`}
            parentApp="aet"
            fromStudent
            assignmentName={assignmentName}
            chapterTitle={q.chapterTitle}
            question={q.questionStem}
            questionNumber={q.questionNumber}
            questionDetails={q}
            options={q.options || []}
            selectedOptions={q.selectedOptions || []}
            attempts={q.attempts || 0}
            isShowAttempt
            status={getQuestionStatus(q.status)}
            sections={sections}
            onStatusChange={(newStatus) => {
              handleStatusChange(q.questionId, topic, q.status, newStatus, assignmentId);
            }}
            footer={footer}
            footerDialogDetails={footerDialogDetails}
            footerCTALinks={footerCTALinks}
            threeDotMenuItems={threeDotMenuItems}
          />
        ))}

        {suggestedRemediation?.show && (
          <DashboardCard
            title={suggestedRemediation?.title}
            subTitle={suggestedRemediation?.subTitle}
            subscriptionText={suggestedRemediation?.subscriptionText}
            classes={{
              content: props.classes?.content,
              subTitle: props.classes?.subTitle
            }}
            components={{
              Root: DashboardCardStudyPrepRoot,
              Header: DashboardCardStudyPrepHeader,
              Title: DashboardCardStudyPrepTitle
            }}
            config={{
              showSubscriptionStatus: suggestedRemediation?.showSubscriptionStatus
            }}
          >
            {assets ? (
              <MultiRemediationComponent
                assets={assets}
                getUserCallback={suggestedRemediation.getUserCallback}
                env={suggestedRemediation.env}
                onSeeInChannelsLinkClick={onSeeInChannelsLinkClick}
                containerProps={remediationContainerProps}
              />
            ) : (
              <EmptyStateCard
                title={suggestedRemediation.subtitle}
                description={suggestedRemediation.description}
                iconName={suggestedRemediation.iconName}
              />
            )}
          </DashboardCard>
        )}
      </ReviewContainer>
    );
  }, [rightContent, selected, showViewFlashcards, sections, flashcards, handleStatusChange]);

  const handleMenuOpen = (_, event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuSelect = React.useCallback((selection) => {
    setSelected(selection);
    onSelect(selection);
    handleMenuClose();
  }, []);


  /**
   * Render Left Accordion List
   * @returns {React.ReactNode}
   */
  const renderLeftAccordionList = (closeMenu) => (
    <LeftAccordionList
      data={leftAccordionData}
      onSelect={closeMenu ? handleMenuSelect : handleSelect}
      showViewFlashcards={showViewFlashcards}
    />
  );

  const renderLeftAccordionListMenu = () => {
    if (isScreenBelow1024) {
      return (
        <Menu
          anchorEl={menuAnchorEl}
          keepMounted
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          slots={{
            paper: LeftAccordionListMenuPaper,
            list: LeftAccordionListMenuList
          }}
        >
          {renderLeftAccordionList(true)}
        </Menu>
      );
    }

    return renderLeftAccordionList();
  };

  return (
    <DashboardCard
      title={headerTitle}
      classes={props.classes}
      components={{
        Root: ReviewCardRoot,
        ActionsLeftContainer: ReviewActionsLeftContainer
      }}
      actions={{
        right: isScreenBelow1024 ? [
          {
            iconName: 'ThreeDotMenuIcon',
            callback: handleMenuOpen
          }] : null
      }}
    >
      <ReviewContainer>
        <ReviewContent>
          <ReviewLeftCol>
            {renderLeftAccordionListMenu()}
          </ReviewLeftCol>
          <ReviewRightCol>
            <RightPannel>
              {rightPanelContent}
            </RightPannel>
          </ReviewRightCol>
        </ReviewContent>
      </ReviewContainer>
    </DashboardCard>
  );
};

ReviewComponent.propTypes = {
  headerTitle: PropTypes.string,
  rightContent: PropTypes.node,
  showViewFlashcards: PropTypes.bool,
  flashcards: PropTypes.array,
  data: PropTypes.array,
  onQuestionStatusChange: PropTypes.func,
  classes: PropTypes.object,
  onSelect: PropTypes.func,
  suggestedRemediationConfig: PropTypes.objectOf(PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    description: PropTypes.string,
    iconName: PropTypes.string,
    getUserCallback: PropTypes.func,
    env: PropTypes.string
  })),
  onSeeInChannelsLinkClick: PropTypes.func,
  remediationContainerProps: PropTypes.object,
  footer: PropTypes.string,
  footerDialogDetails: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    showCloseButton: PropTypes.bool,
    onConfirm: PropTypes.func
  }),
  footerCTALinks: PropTypes.arrayOf(PropTypes.object),
  threeDotMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      callback: PropTypes.func,
      iconName: PropTypes.string
    })
  )
};

ReviewComponent.defaultProps = {
  headerTitle: '',
  rightContent: null,
  showViewFlashcards: false,
  flashcards: [],
  data: [],
  onQuestionStatusChange: undefined,
  onSelect: () => {},
  suggestedRemediationConfig: null,
  onSeeInChannelsLinkClick: () => {},
  remediationContainerProps: {},
  classes: {},
  footer: '',
  footerDialogDetails: {
    title: '',
    content: '',
    confirmText: 'Confirm',
    cancelText: null,
    showCloseButton: false,
    onConfirm: () => {}
  },
  footerCTALinks: [],
  threeDotMenuItems: []
};

export default injectIntl(ReviewComponent);

